var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Page, Text } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import uniqueId from 'lodash/uniqueId';
import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { validate as uuidValidate } from 'uuid';
import { integrationsApi } from 'apis/integrations';
import { analytics } from 'app/utils/segment';
import { articles } from 'utils/elevio';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { SettingsIntegrationsDocumentationButton } from '../../../../../components/documentationButton/documentationButton';
import { CardsTestResult } from '../cardsTestResult/cardsTestResult';
import styles from './cardsTest.module.scss';
import { CardsTestParameterTable } from './components/cardsTestParameterTable/cardsTestParameterTable';
import { getAvailableParameters } from './utils/parameters';
import { generateTestQueryString } from './utils/query';
export const CardsTest = ({ cardType }) => {
    var _a;
    const convertedParameters = getAvailableParameters(cardType);
    const navigate = useAppNavigate();
    const { id } = useAppPathParams(`/settings/integrations/${cardType}-card/test/:id`);
    const [parameters, setParameters] = useState({});
    const [hasWrongId, setHasWrongId] = useState(false);
    const [availableParameters, setAvailableParameters] = useState(convertedParameters);
    const [getCustomCardById, { data: customCard, isLoading: isLoadingCardById },] = integrationsApi.useLazyReadCustomCardIntegrationByIdQuery();
    const [getCustomCardTest, { data: cardTestResult, isLoading: isTestLoading, isError: hasTestError },] = integrationsApi.useLazyReadCustomCardTestIntegrationQuery();
    const title = ((_a = customCard === null || customCard === void 0 ? void 0 : customCard.data) === null || _a === void 0 ? void 0 : _a.title)
        ? `Test: ${customCard.data.title}`
        : `Test: ${cardType} Card`;
    const isCardEmpty = hasWrongId || (!isLoadingCardById && title === '');
    useEffectOnce(() => {
        if (id && uuidValidate(id)) {
            getCustomCardById({ hookId: id });
        }
        else {
            setHasWrongId(true);
        }
    });
    const handleParameterSelect = useCallback(({ id }, key) => {
        setParameters(Object.assign(Object.assign({}, parameters), { [key]: Object.assign(Object.assign({}, parameters[key]), { id }) }));
        setAvailableParameters([
            ...availableParameters.filter((parameter) => parameter.id !== id),
        ]);
    }, [parameters, availableParameters]);
    const handleParameterChange = useCallback((event) => {
        if (!event) {
            return;
        }
        event.preventDefault();
        const { name, value } = event.target;
        setParameters(Object.assign(Object.assign({}, parameters), { [name]: Object.assign(Object.assign({}, parameters[name]), { value }) }));
    }, [parameters]);
    const handleParameterDelete = useCallback((key) => {
        const _a = parameters, _b = key, parameterToDelete = _a[_b], remainingParameters = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        setParameters(remainingParameters);
        setAvailableParameters(convertedParameters.filter((parameter) => !Object.values(remainingParameters).some((item) => item.id === parameter.id)));
        if (id) {
            analytics.settingsIntegrationsCardTestRemoveParameter({
                cardType,
                cardId: id,
            });
        }
    }, [parameters, convertedParameters, id, cardType]);
    const handleTest = useCallback(() => {
        const query = generateTestQueryString(parameters);
        if (id) {
            getCustomCardTest({ hookId: id, query });
            analytics.settingsIntegrationsCardTest({
                cardType,
                cardId: id,
            });
        }
    }, [cardType, parameters, id, getCustomCardTest]);
    if (isCardEmpty) {
        return (_jsx(Page.Section, { children: _jsxs(Text.Paragraph, { children: ["This card does not exist. Please have a look at the existing", ' ', _jsx("span", Object.assign({ onClick: () => navigate({ path: '/settings/integrations' }), className: styles.link }, { children: "cards here" })), "."] }) }));
    }
    return (_jsxs(Page.Section, Object.assign({ title: title, titleMeta: _jsxs(_Fragment, { children: [Object.keys(parameters).length > 0 && (_jsx(Badge, Object.assign({ size: "small", variant: "secondary" }, { children: Object.keys(parameters).length }))), _jsx(SettingsIntegrationsDocumentationButton, { onClick: () => elevioClient.openArticle(articles.settings.cards[cardType]) })] }), controls: _jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ onClick: () => {
                        if (id) {
                            navigate({
                                path: `/settings/integrations/${cardType}-card/edit/:id`,
                                params: {
                                    path: {
                                        id,
                                    },
                                },
                            });
                        }
                    }, variant: "secondary", disabled: isCardEmpty || isTestLoading }, { children: "Edit card" })), _jsx(Button, Object.assign({ disabled: !availableParameters.length, variant: "secondary", onClick: () => {
                        setParameters(Object.assign(Object.assign({}, parameters), { [uniqueId('parameter-')]: {
                                id: null,
                                value: '',
                            } }));
                        if (id) {
                            analytics.settingsIntegrationsCardTestAddParameter({
                                cardType,
                                cardId: id,
                            });
                        }
                    } }, { children: "Add parameter" })), _jsx(Button, Object.assign({ onClick: handleTest, isLoading: isTestLoading, disabled: isCardEmpty, "data-testid": "cards-test-button" }, { children: "Test" }))] }) }, { children: [_jsx(CardsTestParameterTable, { parameters: parameters, availableParameters: availableParameters, handleParameterSelect: handleParameterSelect, handleParameterDelete: handleParameterDelete, handleParameterChange: handleParameterChange }), _jsx(CardsTestResult, { card: customCard === null || customCard === void 0 ? void 0 : customCard.data, isLoading: isTestLoading, hasFailed: hasTestError, testResult: cardTestResult === null || cardTestResult === void 0 ? void 0 : cardTestResult.data })] })));
};
